import { TAB_ITEMS } from '../constants';
import CandidatesUserJobProcessScore from './CandidatesUserJobProcessScore';
import clsx from 'clsx';
import { MaterialIcon } from '@/components/MaterialIcon';
import { Button } from '@/components/Button';
import { MdAdd, MdOpenInNew } from 'react-icons/md';
import CandidatesProfileMenu from './CandidatesProfileMenu';
import { useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import CandidatesDeleteModal from './CandidatesDeleteModal';
import useOnClickOutside from '../../job_change_processes/hooks/useOnClickOutside';
import CandidatesFileUploadModal from './CandidatesFileUploadModal';
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import type { CandidateProfileResponse } from '../types/CandidateProfile';
import { useSetAtom } from 'jotai';
import { isCandidateProfileEditAtom } from '../atom/candidatesAtom';
import CandidatesBoardsMenu from './CandidatesBoardsMenu';
import useFetchCandidateBoards from '../api/useFetchCandidateBoards';
import useActiveUsers from '../../active_users/hooks/useActiveUsers';
import UsersMenu from '@/features/agent/components/UsersMenu';
import useUpdateCandidateProfile from '../api/useUpdateCandidateProfile';
import useFetchCandidateEndpoint from '../api/useFetchCandidateEndpoint';
import { showErrorToast } from '../../job_change_processes/lib/toast';
import CandidatesInterviewDropdown from './CandidatesInterviewDropdown';
import CandidatesInterviewTranscriptDialog from './CandidatesInterviewTranscriptDialog';

// 文字数がmaxを超える場合に省略記号を付与して切り詰める
const truncateText = (text: string, max: number) => {
  return text.length > max ? text.slice(0, max) + '…' : text;
};

type Props = {
  candidateId: number;
  tab: (typeof TAB_ITEMS)[number]['value'];
  candidateName: string;
  organizationUserIds?: number[];
  currentUserId?: number;
  companyName: string | null;
  jobPosition: string | null;
  age: string;
  scores: {
    suggest: number;
    documentScreening: number;
    interview: number;
    offer: number;
    close: number;
  };
  files: {
    id: number;
    filename: string;
    url: string;
  }[];
  candidateBoard?: {
    id?: number;
    title?: string;
    colorType?: 'gray' | 'red' | 'purple' | 'blue' | 'yellow' | 'green';
  };
  setTab: (val: (typeof TAB_ITEMS)[number]['value']) => void;
  candidateProfileRefetch: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<CandidateProfileResponse, Error>>;
};

export default function CandidatesProfileTab({
  candidateId,
  tab,
  setTab,
  candidateName,
  organizationUserIds = [],
  currentUserId,
  companyName,
  jobPosition,
  age,
  scores,
  files,
  candidateBoard = undefined,
  candidateProfileRefetch,
}: Props) {
  const setIsCandidateProfileEdit = useSetAtom(isCandidateProfileEditAtom);
  const { data: candidateBoards } = useFetchCandidateBoards();
  const { data: activeUsersData } = useActiveUsers();
  const activeUsers = activeUsersData?.activeUsers ?? [];
  const { mutateAsync: updateCandidate } = useUpdateCandidateProfile();
  const { data: candidateEndpoint } = useFetchCandidateEndpoint({ id: candidateId });
  const mypageUrl = candidateEndpoint?.candidate_endpoint?.url;

  const [isProfileDeleteShow, setIsProfileDeleteShow] = useState(false);
  const [isFileUploadShow, setIsFileUploadShow] = useState(false);
  const [isTranscriptDialogOpen, setIsTranscriptDialogOpen] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  useOnClickOutside(menuRef, () => setIsMenuOpen(false));

  const onClickDelteMenu = () => {
    setIsMenuOpen(false);
    setIsProfileDeleteShow(true);
  };

  const onProfileEdit = () => {
    setIsMenuOpen(false);
    setTab('profile');
    setIsCandidateProfileEdit(true);
  };

  const handleSelectAssignees = async (userIds: number[]) => {
    try {
      await updateCandidate({
        candidateProfileId: candidateId,
        organization_user_ids: userIds,
      });
      await candidateProfileRefetch();
    } catch (e) {
      showErrorToast('担当者の更新に失敗しました');
    }
  };

  return (
    <div className="bg-white shadow-[0px_3px_5px_-3px_#00000040]">
      <div className="px-8">
        <div className="flex min-w-0 flex-col gap-xs">
          <div className="flex h-8 items-center justify-between gap-sm">
            <div className="flex items-center gap-sm">
              <p className="text-heading-md whitespace-nowrap">{candidateName}</p>
              <UsersMenu
                activeUsers={activeUsers}
                selectedUserIds={organizationUserIds}
                onSelect={handleSelectAssignees}
                currentUserId={currentUserId}
              />
              <CandidatesBoardsMenu
                candidatesBoards={candidateBoards?.records ?? []}
                candidateProfileId={candidateId}
                candidateProfileRefetch={candidateProfileRefetch}
                candidateBoard={candidateBoard}
              />
            </div>
            <div className="flex items-center gap-xxs">
              <CandidatesInterviewDropdown
                variant="header"
                items={[
                  {
                    label: '面談情報を見る',
                    onSelect: () =>
                      navigate({
                        to: '/agent/candidates/$candidateProfileId',
                        params: { candidateProfileId: String(candidateId) },
                        search: (prev) => ({ ...prev, tab: 'timeline', filter: 'system' }),
                      }),
                  },
                  {
                    label: '面談情報を入力',
                    onSelect: () =>
                      navigate({
                        to: '/agent/candidates/$candidateProfileId/interview_logs/new',
                        params: { candidateProfileId: String(candidateId) },
                      }),
                  },
                  { label: '文字起こしを追加', onSelect: () => setIsTranscriptDialogOpen(true) },
                ]}
              />
              {mypageUrl && (
                <Button asChild color="tertiary" size="sm">
                  <a href={mypageUrl} target="_blank" rel="noopener noreferrer">
                    <MdOpenInNew size={16} />
                    求職者マイページ
                  </a>
                </Button>
              )}
              <div className="relative" ref={menuRef}>
                <Button
                  type="button"
                  color="secondary"
                  size="sm"
                  iconOnly
                  active={isMenuOpen}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMenuOpen((prev) => !prev);
                  }}
                >
                  <img src="/agent/candidates/icon-more.svg" width={24} height={24} alt="" />
                </Button>
                {isMenuOpen && (
                  <div className="absolute top-10 right-0 z-50">
                    <CandidatesProfileMenu
                      deleteOpen={onClickDelteMenu}
                      onProfileEdit={onProfileEdit}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-md">
            <div className="flex flex-col items-start gap-sm">
              <div className="flex gap-6">
                {companyName && (
                  <div className="flex items-center gap-2">
                    <MaterialIcon
                      name="apartment"
                      filled
                      className="text-text/placeholder text-xl"
                    />
                    <div className="group relative inline-block">
                      <p className="text-xs">{truncateText(companyName, 11)}</p>
                      {companyName.length > 11 && (
                        <div className="absolute bottom-full left-0 z-10 mb-2 hidden w-max transform rounded bg-black p-2 text-xs text-white group-hover:block">
                          {companyName}
                          <div className="absolute top-full left-4 size-0 border-8 border-solid border-transparent border-t-black"></div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {jobPosition && (
                  <div className="flex items-center gap-2">
                    <MaterialIcon name="work" filled className="text-text/placeholder text-xl" />
                    <div className="group relative inline-block">
                      <p className="text-xs">{truncateText(jobPosition, 11)}</p>
                      {jobPosition.length > 11 && (
                        <div className="absolute bottom-full left-0 z-10 mb-2 hidden w-max transform rounded bg-black p-2 text-xs text-white group-hover:block">
                          {jobPosition}
                          <div className="absolute top-full left-4 size-0 border-8 border-solid border-transparent border-t-black"></div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {age && (
                  <div className="flex items-center gap-2">
                    <MaterialIcon name="person" filled className="text-text/placeholder text-xl" />
                    <p className="text-xs">{age}歳</p>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                {files.map((f) => {
                  return (
                    <div key={f.id} className="group relative">
                      <button
                        type="button"
                        className="flex size-xl items-center justify-center rounded border border-solid border-border bg-white hover:bg-tag-gray"
                        onClick={() => window.open(f.url, '_blank')}
                      >
                        <img src="/agent/candidates/icon-file.svg" alt="" width={16} height={16} />
                      </button>
                      <div className="absolute top-full left-0 z-10 mt-2 hidden w-max rounded bg-black p-2 text-xs text-white group-hover:block">
                        {f.filename}
                        <div className="absolute bottom-full left-4 size-0 border-8 border-solid border-transparent border-b-black"></div>
                      </div>
                    </div>
                  );
                })}
                <Button
                  type="button"
                  color="secondary"
                  size="sm"
                  onClick={() => setIsFileUploadShow(true)}
                >
                  <MdAdd size={16} />
                  ファイルを追加
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-xs">
              <CandidatesUserJobProcessScore text="提案" score={scores.suggest} size="small" />
              <CandidatesUserJobProcessScore
                text="書類選考"
                score={scores.documentScreening}
                size="small"
              />
              <CandidatesUserJobProcessScore text="選考中" score={scores.interview} size="small" />
              <CandidatesUserJobProcessScore text="内定" score={scores.offer} size="small" />
              <CandidatesUserJobProcessScore text="内定承諾" score={scores.close} size="small" />
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-around">
        {TAB_ITEMS.map((item) => (
          <button
            type="button"
            key={item.value}
            className={clsx(
              'h-11 w-full cursor-pointer border-0 border-b border-solid border-b-border bg-transparent px-lg text-sm hover:font-bold hover:text-primary',
              tab === item.value && 'border-b-2 border-b-primary font-bold text-primary'
            )}
            onClick={() => setTab(item.value)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <CandidatesFileUploadModal
        candidateId={candidateId}
        open={isFileUploadShow}
        onOpenChange={setIsFileUploadShow}
        files={files}
        candidateProfileRefetch={candidateProfileRefetch}
      />
      <CandidatesDeleteModal
        candidateId={candidateId}
        open={isProfileDeleteShow}
        onOpenChange={setIsProfileDeleteShow}
      />
      <CandidatesInterviewTranscriptDialog
        candidateProfileId={candidateId}
        isOpen={isTranscriptDialogOpen}
        onOpenChange={setIsTranscriptDialogOpen}
      />
    </div>
  );
}
